import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { defineStore } from 'pinia';
import DealAPI from 'dashboard/api/deals';
import PipelineAPI from 'dashboard/api/pipelines';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const camelize = data =>
  camelcaseKeys(data || {}, { deep: true, stopPaths: ['custom_attributes'] });

export const useDealsStore = defineStore('deals', {
  state: () => ({
    pipelines: [],
    activePipelineId: null,
    deals: [],
    uiFlags: {
      fetchingPipelines: false,
      fetchingDeals: false,
      creatingPipeline: false,
      creatingDeal: false,
      updatingDeal: false,
      movingDeal: false,
    },
  }),

  getters: {
    activePipeline: state =>
      state.pipelines.find(p => p.id === state.activePipelineId) || null,

    stages() {
      return this.activePipeline?.stages || [];
    },

    // Nomi già usati in "le mie società": alimentano l'autocomplete così i brand si
    // riusano invece di riscriverli ogni volta.
    ownerCompanySuggestions: state =>
      [
        ...new Set(
          state.deals.flatMap(deal => deal.ownerCompanies || []).filter(Boolean)
        ),
      ].sort((a, b) => a.localeCompare(b)),

    // Il kanban legge da qui: una lista ordinata di deal per ogni colonna.
    dealsByStageId: state =>
      state.deals.reduce((grouped, deal) => {
        const column = grouped[deal.pipelineStageId] || [];
        column.push(deal);
        grouped[deal.pipelineStageId] = column.sort(
          (a, b) => a.position - b.position
        );
        return grouped;
      }, {}),
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    upsertDeal(deal) {
      const index = this.deals.findIndex(d => d.id === deal.id);
      if (index === -1) this.deals.push(deal);
      else this.deals[index] = deal;
    },

    async fetchPipelines() {
      this.setUIFlag({ fetchingPipelines: true });
      try {
        const {
          data: { payload },
        } = await PipelineAPI.get();
        this.pipelines = camelize(payload);
        if (!this.activePipelineId && this.pipelines.length) {
          const preferred =
            this.pipelines.find(p => p.isDefault) || this.pipelines[0];
          this.activePipelineId = preferred.id;
        }
        return this.pipelines;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingPipelines: false });
      }
    },

    setActivePipeline(pipelineId) {
      this.activePipelineId = Number(pipelineId);
    },

    // Crea una pipeline (il backend le aggancia in automatico le colonne standard) e la
    // rende subito attiva, cosi' la board si popola senza un secondo giro di fetch.
    async createPipeline(attrs) {
      this.setUIFlag({ creatingPipeline: true });
      try {
        const {
          data: { payload },
        } = await PipelineAPI.create({
          pipeline: snakecaseKeys(attrs, { deep: true }),
        });
        const pipeline = camelize(payload);
        this.pipelines.push(pipeline);
        this.activePipelineId = pipeline.id;
        return pipeline;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingPipeline: false });
      }
    },

    // `silent` evita lo spinner a tutta board: serve per i refresh in sottofondo
    // (es. dopo aver collegato/scollegato una conversazione dalla scheda del deal).
    async fetchDeals(params = {}, { silent = false } = {}) {
      if (!silent) this.setUIFlag({ fetchingDeals: true });
      try {
        const {
          data: { payload },
        } = await DealAPI.get(snakecaseKeys(params));
        this.deals = camelize(payload);
        return this.deals;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        if (!silent) this.setUIFlag({ fetchingDeals: false });
      }
    },

    async createDeal(dealAttrs) {
      this.setUIFlag({ creatingDeal: true });
      try {
        const { conversationId, ...attrs } = dealAttrs;
        const {
          data: { payload },
        } = await DealAPI.create({
          deal: snakecaseKeys(attrs, { deep: true }),
          ...(conversationId && { conversation_id: conversationId }),
        });
        const deal = camelize(payload);
        this.upsertDeal(deal);
        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingDeal: false });
      }
    },

    async updateDeal(id, dealAttrs) {
      this.setUIFlag({ updatingDeal: true });
      try {
        const {
          data: { payload },
        } = await DealAPI.update(id, {
          deal: snakecaseKeys(dealAttrs, { deep: true }),
        });
        const deal = camelize(payload);
        this.upsertDeal(deal);
        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingDeal: false });
      }
    },

    // Riordina la colonna in locale prima della chiamata: il drag deve sembrare istantaneo.
    applyLocalMove(dealId, stageId, index) {
      const deal = this.deals.find(d => d.id === dealId);
      if (!deal) return;

      deal.pipelineStageId = stageId;
      const column = this.deals
        .filter(d => d.pipelineStageId === stageId && d.id !== dealId)
        .sort((a, b) => a.position - b.position);
      column.splice(Math.min(index, column.length), 0, deal);
      column.forEach((d, position) => {
        d.position = position;
      });
    },

    async moveDeal({ dealId, stageId, index }) {
      const snapshot = this.deals.map(d => ({ ...d }));
      this.applyLocalMove(dealId, stageId, index);
      this.setUIFlag({ movingDeal: true });
      try {
        const {
          data: { payload },
        } = await DealAPI.move(dealId, stageId, index);
        this.upsertDeal(camelize(payload));
        return true;
      } catch (error) {
        this.deals = snapshot;
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ movingDeal: false });
      }
    },

    async deleteDeal(id) {
      try {
        await DealAPI.delete(id);
        this.deals = this.deals.filter(d => d.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async linkConversation(dealId, conversationId) {
      try {
        await DealAPI.linkConversation(dealId, conversationId);
        return true;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async unlinkConversation(dealId, conversationId) {
      try {
        await DealAPI.unlinkConversation(dealId, conversationId);
        return true;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async listDealConversations(dealId) {
      const {
        data: { payload },
      } = await DealAPI.listConversations(dealId);
      return camelize(payload);
    },

    // Cambio stage dal fuori-board (es. dalla conversazione): spostiamo il deal in
    // coda alla colonna scelta. move_to! lato server riclampa l'indice, quindi un
    // numero alto significa "in fondo".
    async changeDealStage(dealId, stageId) {
      try {
        const {
          data: { payload },
        } = await DealAPI.move(dealId, stageId, 99999);
        const deal = camelize(payload);
        this.upsertDeal(deal);
        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    // --- Gestione fasi (colonne) ------------------------------------------------
    // Aggiornano in locale le stages della pipeline per una board reattiva senza
    // ricaricare tutto.
    replacePipelineStages(pipelineId, stages) {
      const pipeline = this.pipelines.find(p => p.id === pipelineId);
      if (pipeline) pipeline.stages = stages;
    },

    async createStage(pipelineId, attrs) {
      try {
        const {
          data: { payload },
        } = await PipelineAPI.createStage(
          pipelineId,
          snakecaseKeys(attrs, { deep: true })
        );
        const stage = camelize(payload);
        const pipeline = this.pipelines.find(p => p.id === pipelineId);
        if (pipeline) {
          this.replacePipelineStages(
            pipelineId,
            [...(pipeline.stages || []), stage].sort(
              (a, b) => a.position - b.position
            )
          );
        }
        return stage;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async updateStage(pipelineId, stageId, attrs) {
      try {
        const {
          data: { payload },
        } = await PipelineAPI.updateStage(
          pipelineId,
          stageId,
          snakecaseKeys(attrs, { deep: true })
        );
        const stage = camelize(payload);
        const pipeline = this.pipelines.find(p => p.id === pipelineId);
        if (pipeline) {
          this.replacePipelineStages(
            pipelineId,
            (pipeline.stages || [])
              .map(s => (s.id === stage.id ? stage : s))
              .sort((a, b) => a.position - b.position)
          );
        }
        return stage;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async deleteStage(pipelineId, stageId, moveDealsToStageId = null) {
      try {
        await PipelineAPI.deleteStage(pipelineId, stageId, moveDealsToStageId);
        const pipeline = this.pipelines.find(p => p.id === pipelineId);
        const remaining = (pipeline?.stages || [])
          .filter(s => s.id !== stageId)
          .sort((a, b) => a.position - b.position);
        const fallbackId = moveDealsToStageId || remaining[0]?.id;
        this.replacePipelineStages(pipelineId, remaining);
        // I deal della colonna eliminata sono stati riassegnati lato server:
        // rispecchiamolo in locale così la board resta coerente.
        if (fallbackId) {
          this.deals = this.deals.map(deal =>
            deal.pipelineStageId === stageId
              ? { ...deal, pipelineStageId: fallbackId }
              : deal
          );
        }
        return true;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },

    async reorderStages(pipelineId, stageIds) {
      try {
        const {
          data: { payload },
        } = await PipelineAPI.reorderStages(pipelineId, stageIds);
        this.replacePipelineStages(pipelineId, camelize(payload));
        return true;
      } catch (error) {
        return throwErrorMessage(error);
      }
    },
  },
});
